/**
 * Hook that bridges the Fricamp app with Android Auto.
 * Sends spots, position, route, map style and language to the car display.
 * Listens for car-side events (spot selected, navigate request).
 *
 * Android Auto is a Premium-only feature. Data is only pushed to the car when
 * the user has premium access (paid subscription, admin, or active tester).
 * Connection state is still tracked even without premium so the phone UI can
 * surface an upsell when a non-premium user plugs in.
 */
import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import type { AppUser, CampingSpot, Coordinates, MapBounds, RouteFollowState } from '../types';
import {
  sendSpotsToAuto,
  sendPositionToAuto,
  sendRouteToAuto,
  clearAutoRoute,
  clearAutoData,
  setAutoMapStyle,
  setAutoLanguage,
  setAutoSubscriptionGate,
  onAutoSpotSelected,
  onAutoNavigateRequest,
  onAutoCheckIn,
  onAutoMapStyleChange,
  onAutoSyncRequest,
  onAutoConnectionChanged,
  isAndroidAutoConnected,
} from '../services/androidAutoService';
import { hasPremiumAccess } from '../services/accessControl';
import { getDistance } from '../services/geometryUtils';

interface UseAndroidAutoOptions {
  /** Current user — gates whether data is actually pushed to the car. */
  user: AppUser | null;
  spots: CampingSpot[];
  /** Current phone-map viewport — used to mirror visible markers to the car. */
  viewBounds?: MapBounds | null;
  userPosition: Coordinates | null;
  userHeading?: number;
  userSpeed?: number;
  mapStyle: 'topo' | 'satellite';
  language: string;
  followRouteState: RouteFollowState | null;
  onSpotSelected?: (spotId: string) => void;
  onNavigateRequest?: (spotId: string) => void;
  onCheckIn?: (spotId: string) => void;
  onMapStyleChange?: (style: 'topo' | 'satellite') => void;
  onSyncRequest?: () => void;
}

const AVG_SPEED_MS = 50 / 3.6;
const isAndroid = Capacitor.getPlatform() === 'android';

// Android Auto is FREE for now. Google Play's Car App Quality guidelines expect
// the core navigation experience to be usable without a paywall — gating the
// whole AA experience behind Premium is why a reviewer (no subscription) saw an
// empty car screen and "couldn't start turn-by-turn directions". Once the build
// is approved we will flip this back to false and re-gate AA — at that point a
// non-premium user who plugs in should get a clear "pay to use" screen on the
// car (not silent emptiness). Until then, treat every user as having access.
const AUTO_IS_FREE = true;

export interface AndroidAutoState {
  /** True while an Android Auto head unit (or DHU) is connected to the app. */
  isAutoConnected: boolean;
  /** True when the current user has Premium-tier access to the AA feature. */
  hasAccess: boolean;
}

export function useAndroidAuto({
  user,
  spots,
  viewBounds,
  userPosition,
  userHeading = 0,
  userSpeed = 0,
  mapStyle,
  language,
  followRouteState,
  onSpotSelected,
  onNavigateRequest,
  onCheckIn,
  onMapStyleChange,
  onSyncRequest,
}: UseAndroidAutoOptions): AndroidAutoState {
  // While AUTO_IS_FREE, every user has access (see flag above). hasPremiumAccess
  // is still evaluated so flipping the flag back to false restores gating with
  // no other change here.
  const hasAccess = AUTO_IS_FREE || hasPremiumAccess(user);
  const [isAutoConnected, setIsAutoConnected] = useState<boolean>(false);

  // Keep latest hasAccess in a ref so the connection listener (registered once
  // with empty deps) reads the current value when AA reconnects mid-session.
  const hasAccessRef = useRef(hasAccess);
  hasAccessRef.current = hasAccess;
  // Use refs for all values needed in event listeners to avoid stale closures
  const spotsRef = useRef(spots);
  const userPositionRef = useRef(userPosition);
  const userHeadingRef = useRef(userHeading);
  const userSpeedRef = useRef(userSpeed);
  const followRouteRef = useRef(followRouteState);
  const onSpotSelectedRef = useRef(onSpotSelected);
  const onNavigateRequestRef = useRef(onNavigateRequest);
  const onCheckInRef = useRef(onCheckIn);
  const onMapStyleChangeRef = useRef(onMapStyleChange);
  const onSyncRequestRef = useRef(onSyncRequest);
  const lastSpotCountRef = useRef(0);
  const lastPositionRef = useRef('');
  const lastRouteIdRef = useRef<string | null>(null);

  // Keep refs current
  spotsRef.current = spots;
  userPositionRef.current = userPosition;
  userHeadingRef.current = userHeading;
  userSpeedRef.current = userSpeed;
  followRouteRef.current = followRouteState;
  onSpotSelectedRef.current = onSpotSelected;
  onNavigateRequestRef.current = onNavigateRequest;
  onCheckInRef.current = onCheckIn;
  onMapStyleChangeRef.current = onMapStyleChange;
  onSyncRequestRef.current = onSyncRequest;

  // Re-check AA connection state immediately when navigation starts.
  // The 2s poll can miss the window between AA attaching and user starting a
  // route; this catches that race so the phone enters browse-only mode at
  // the same instant the car takes over.
  useEffect(() => {
    if (!isAndroid) return;
    if (!followRouteState) return;
    isAndroidAutoConnected().then(c => setIsAutoConnected(prev => prev === c ? prev : c));
  }, [followRouteState?.routeId]);

  // Reset the sent-route memo whenever AA reconnects. This forces the next
  // route-sync effect to re-push the route (otherwise lastRouteIdRef holds
  // the id from a previous session and we'd skip the resend).
  const prevAutoConnectedRef = useRef(false);
  useEffect(() => {
    if (!isAndroid) return;
    if (isAutoConnected && !prevAutoConnectedRef.current) {
      lastRouteIdRef.current = null;
    }
    prevAutoConnectedRef.current = isAutoConnected;
  }, [isAutoConnected]);

  // Push current premium state to the native plugin every time it changes.
  // Native-side defense-in-depth: even if some other code path tried to call
  // updateNearbySpots / showRoute, the plugin will drop it when the gate is
  // closed. Also clears car-side data the instant access is lost mid-session.
  useEffect(() => {
    if (!isAndroid) return;
    setAutoSubscriptionGate(hasAccess);
    if (!hasAccess) {
      lastRouteIdRef.current = null;
      lastPositionRef.current = '';
      lastSpotCountRef.current = 0;
      clearAutoData();
    }
  }, [hasAccess]);

  // Sync active route to Android Auto when followRouteState changes OR when
  // AA reconnects (lastRouteIdRef was just cleared above).
  useEffect(() => {
    if (!isAndroid) return;
    if (!hasAccess) return; // Premium-only — non-premium users get no route on AA
    const routeId = followRouteState?.routeId ?? null;

    if (!followRouteState && lastRouteIdRef.current) {
      lastRouteIdRef.current = null;
      clearAutoRoute();
      return;
    }

    if (routeId === lastRouteIdRef.current) return;
    if (!isAutoConnected) return; // No point sending until AA is listening

    if (followRouteState && followRouteState.routeCoords.length > 1) {
      lastRouteIdRef.current = routeId;
      const distanceM = followRouteState.routeTotalDistanceM;
      if (distanceM <= 0) return;
      const coords: [number, number][] = followRouteState.routeCoords.map(c => [c.longitude, c.latitude]);
      const durationSec = distanceM / AVG_SPEED_MS;
      // Match the route endpoint to a known Fricamp spot so the car can show
      // the destination card with thumbnail/score. Tries name match first
      // (handles spots far from user that are still the explicit target),
      // then nearest-endpoint within 300m as a fallback.
      const lastCoord = followRouteState.routeCoords[followRouteState.routeCoords.length - 1];
      let destSpot = spotsRef.current.find(s => s.name && s.name === followRouteState.routeName);
      if (!destSpot && lastCoord) {
        destSpot = spotsRef.current.find(s =>
          s.location &&
          getDistance(s.location.latitude, s.location.longitude, lastCoord.latitude, lastCoord.longitude) < 300
        );
      }
      sendRouteToAuto(coords, distanceM, durationSec, followRouteState.routeName, undefined, destSpot?.id);
    }
  }, [followRouteState?.routeId, isAutoConnected, hasAccess]);

  const lastPositionSentRef = useRef<{lat: number, lon: number} | null>(null);

  // Send spots when they change or position moves significantly.
  // Also re-send when an active route's identity changes so the route-corridor
  // spots (used by the "next Fricamp along route" overlay) get refreshed.
  useEffect(() => {
    if (!isAndroid) return;
    if (!hasAccess) return; // Premium-only — non-premium users get no spots on AA
    console.log('[AndroidAuto] Hook spots effect trigger', { spotCount: spots.length, hasPos: !!userPosition });

    if (!userPosition) {
      console.warn('[AndroidAuto] Missing userPosition, cannot send spots');
      return;
    }

    lastSpotCountRef.current = spots.length;
    lastPositionSentRef.current = { lat: userPosition.latitude, lon: userPosition.longitude };

    const routeCoords = followRouteState?.routeCoords ?? null;
    const bounds = viewBounds ?? null;
    const timer = setTimeout(() => {
      sendSpotsToAuto(spots, userPosition, routeCoords, bounds);
    }, 1000);
    return () => clearTimeout(timer);
  }, [hasAccess, spots.length, userPosition?.latitude, userPosition?.longitude, followRouteState?.routeId, viewBounds?.north, viewBounds?.south, viewBounds?.east, viewBounds?.west]);

  // Send position updates (throttled)
  useEffect(() => {
    if (!isAndroid) return;
    if (!hasAccess) return;
    console.log('[AndroidAuto] Hook position update', { hasPos: !!userPosition });
    if (!userPosition) return;

    sendPositionToAuto(userPosition, userHeading, userSpeed);
  }, [hasAccess, userPosition?.latitude, userPosition?.longitude, userHeading, userSpeed]);

  // Send map style changes
  useEffect(() => {
    if (!isAndroid) return;
    if (!hasAccess) return;
    setAutoMapStyle(mapStyle);
  }, [hasAccess, mapStyle]);

  // Send language changes
  useEffect(() => {
    if (!isAndroid) return;
    if (!hasAccess) return;
    setAutoLanguage(language);
  }, [hasAccess, language]);

  // Listen for car-side events (register once, use refs for current values)
  useEffect(() => {
    if (!isAndroid) return;

    const cleanups: (() => void)[] = [];
    let mounted = true;

    // Helper that properly handles async listener registration
    const registerListener = (
      register: (handler: (id: string) => void) => (() => void) | null,
      handler: (id: string) => void,
    ) => {
      const cleanup = register(handler);
      if (cleanup) cleanups.push(cleanup);
    };

    registerListener(onAutoSpotSelected, (spotId) => {
      onSpotSelectedRef.current?.(spotId);
    });

    registerListener(onAutoNavigateRequest, (spotId) => {
      onNavigateRequestRef.current?.(spotId);
    });

    registerListener(onAutoCheckIn, (spotId) => {
      onCheckInRef.current?.(spotId);
    });

    // mapStyleChanged uses a different signature than registerListener expects (style string, not id)
    const mapStyleCleanup = onAutoMapStyleChange((style) => {
      onMapStyleChangeRef.current?.(style);
    });
    if (mapStyleCleanup) cleanups.push(mapStyleCleanup);

    // syncRequested has no payload — wrap to match registerListener signature
    const syncCleanup = onAutoSyncRequest(() => {
      onSyncRequestRef.current?.();
    });
    if (syncCleanup) cleanups.push(syncCleanup);

    // Initial state check — we may have been spawned after Auto is already on
    isAndroidAutoConnected().then(c => { if (mounted) setIsAutoConnected(c); });

    // Backup polling — connectionChanged events are flaky after app reinstall
    // (event can fire before listeners attach). 2s feels snappy and is still
    // cheap. setState callback dedup ensures no-op renders when stable.
    const pollHandle = setInterval(async () => {
      if (!mounted) return;
      try {
        const c = await isAndroidAutoConnected();
        setIsAutoConnected(prev => prev === c ? prev : c);
      } catch { /* offline, plugin gone — ignore */ }
    }, 2000);
    cleanups.push(() => clearInterval(pollHandle));

    // Connection changed — re-send fresh data from refs and update local state.
    // We always update the connection flag (so the phone UI can show an upsell
    // pill for non-premium users who plug in), but only re-push data when the
    // current user has premium access.
    const connCleanup = onAutoConnectionChanged((connected) => {
      if (!mounted) return;
      console.log(`[AndroidAuto] Connection state changed: ${connected}`);
      setIsAutoConnected(connected);
      if (connected && hasAccessRef.current) {
        if (spotsRef.current.length > 0 && userPositionRef.current) {
          sendSpotsToAuto(spotsRef.current, userPositionRef.current,
            followRouteRef.current?.routeCoords ?? null);
        }
        if (userPositionRef.current) {
          sendPositionToAuto(userPositionRef.current, userHeadingRef.current, userSpeedRef.current);
        }
        setAutoMapStyle(mapStyle);
        setAutoLanguage(language);
        // Re-send active route if one is in progress — AA was likely off when
        // the user started navigating, or it lost state on disconnect. Clear
        // the route-id ref so the next route-sync effect will push the route
        // again on this reconnect.
        const fr = followRouteRef.current;
        if (fr && fr.routeCoords.length > 1) {
          lastRouteIdRef.current = null; // force re-send by the route-sync effect
          const distanceM = fr.routeTotalDistanceM;
          if (distanceM > 0) {
            const coords: [number, number][] = fr.routeCoords.map(c => [c.longitude, c.latitude]);
            const durationSec = distanceM / AVG_SPEED_MS;
            const lastCoord = fr.routeCoords[fr.routeCoords.length - 1];
            let destSpot = spotsRef.current.find(s => s.name && s.name === fr.routeName);
            if (!destSpot && lastCoord) {
              destSpot = spotsRef.current.find(s =>
                s.location &&
                getDistance(s.location.latitude, s.location.longitude, lastCoord.latitude, lastCoord.longitude) < 300
              );
            }
            sendRouteToAuto(coords, distanceM, durationSec, fr.routeName, undefined, destSpot?.id);
            lastRouteIdRef.current = fr.routeId;
          }
        }
      }
    });
    if (connCleanup) cleanups.push(connCleanup);

    return () => {
      mounted = false;
      cleanups.forEach(c => c());
    };
  }, []);

  return { isAutoConnected, hasAccess };
}
